import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckOutlined, CloseOutlined, GlobalOutlined, LogoutOutlined, MenuOutlined, UserOutlined, UserSwitchOutlined } from "@ant-design/icons";
import Logotype from "../../Logotype/Logotype";
import { Divider, Menu, Typography } from "antd";
import { cloneElement, useMemo, useState } from "react";
import IconActionWrapper from "./IconActionWrapper.component";
import { productColors } from "../../../assets/variables";
import useTopbarLayoutStore from "../_store/TopbarLayout.store";
import useSlot from "../../../hooks/slot.hook";
import ScrollOverlay from "../../Overflow/ScrollOverlay.component";
/**
 * Displays a mobile hamburger menu.
 * Usage: `<TopbarContent.HamburgerMenu />`
 */
export const HamburgerMenu = ({ slots }) => {
    const { navigation, actions, footer } = slots;
    const [menuOpen, setMenuOpen] = useState(false);
    const { currentProduct, centerItems, showTimeEditIcon } = useTopbarLayoutStore();
    const CenterMenu = useSlot({
        element: centerItems,
        displayName: "NavigationMenuWrapper"
    });
    const defaultNaviation = useMemo(() => {
        if (navigation)
            return navigation;
        if (!CenterMenu)
            return centerItems;
        // If the `center` items is a menu, it could be set to the wrong horizontal mode.
        return cloneElement(CenterMenu, Object.assign(Object.assign({}, CenterMenu.props), { mode: "inline" }));
    }, [slots.navigation, centerItems]);
    return (_jsxs(_Fragment, { children: [showTimeEditIcon && (_jsx(IconActionWrapper, { className: "te-hidden sm:te-flex", children: _jsx(Logotype, { alt: "TimeEdit Logo", id: "TE", size: 24 }) })), _jsx(IconActionWrapper, { className: `
          sm:te-hidden te-transition-colors te-duration-300
          ${menuOpen ? "te-bg-te-colorPrimaryBg" : ""}
        `, onClick: () => setMenuOpen(!menuOpen), children: menuOpen ? _jsx(CloseOutlined, {}) : _jsx(MenuOutlined, {}) }), _jsx("div", { className: `
          te-h-[calc(100vh-51px)] te-right-0 te-w-screen te-top-[51px] te-fixed te-pointer-events-none
          te-z-0 te-transition-opacity te-duration-300 !te-bg-te-colorBgBase
          ${menuOpen ? "te-opacity-50" : "te-opacity-0"}
          sm:te-hidden
        `, style: currentProduct && { background: `${productColors[currentProduct]}` } }), _jsxs("div", { className: `
          te-fixed te-right-0 te-top-[51px] te-h-[calc(100vh-51px)]
          te-w-screen te-z-10 te-bg-te-colorBgBase
          te-transition-transform te-duration-300
          sm:te-hidden te-flex te-flex-col
          ${menuOpen ? "te-translate-x-0" : "te-translate-x-full"}
        `, children: [defaultNaviation && (_jsxs("div", { className: "te-overflow-y-auto te-relative", children: [defaultNaviation, _jsx(ScrollOverlay, { direction: "vertical", className: "te-sticky te-w-full te-bottom-0" })] })), actions && (_jsxs(_Fragment, { children: [_jsx(Divider, { type: "horizontal", className: "te-mt-0 te-mb-2" }), _jsx("div", { className: "te-shrink te-overflow-y-auto", children: actions })] })), (showTimeEditIcon || footer) && (_jsxs(_Fragment, { children: [_jsx(Divider, { type: "horizontal", className: "te-my-2" }), _jsxs("div", { className: "te-pt-2 te-pb-4", children: [footer, _jsx("div", { className: "te-flex te-justify-center", children: showTimeEditIcon && (_jsx(Logotype, { alt: "TimeEdit logo", id: "TEWithText", size: 32 })) })] })] }))] })] }));
};
/**
 * Display an inline Language navigation action menu
 * Usage: `<TopbarContent.LanguageNavigationAction />`
 */
export const LanguageNavigationAction = ({ currentLanguage, onChange, label = "Language", languages }) => {
    return (_jsx(Menu, { items: [
            {
                className: "te-hamburger-language-action !te-text-colorTextBase",
                icon: _jsx(GlobalOutlined, { className: "!te-text-colorTextBase" }),
                label: _jsx("span", { className: "!te-text-colorTextBase", children: label }),
                key: "Language",
                children: languages === null || languages === void 0 ? void 0 : languages.map((lang) => ({
                    key: lang,
                    onClick: () => onChange(lang),
                    label: (_jsxs("div", { className: "te-relative", children: [_jsx("div", { className: `
                      te-transition-opacity te-absolute te-left-0 te-top-0 te-h-full te-duration-300
                      ${currentLanguage === lang ? "te-opacity-100" : "te-opacity-0"}
                    `, children: _jsx(CheckOutlined, {}) }), _jsx("div", { className: `
                      te-transition-transform te-duration-300
                      ${currentLanguage === lang ? "te-translate-x-5" : "te-translate-x-0"}
                    `, children: lang })] }))
                }))
            }
        ], selectedKeys: [currentLanguage], mode: "inline" }));
};
/**
 * Display an inline user navigation action menu
 * Usage: `<TopbarContent.UserNavigationAction />`
 */
export const UserNavigationAction = ({ onLogout, authUrl, email, labels = {
    account: "Account",
    switchProfile: "Switch Profile",
    logOut: "Log out"
}, role, name, customerSignature, profiles }) => {
    const userNavigationActionChildren = useMemo(() => {
        const children = [
            {
                key: "hamburgerLogout",
                onClick: onLogout,
                className: "!te-pointer-events-none !te-w-full !te-m-0 !te-bg-te-colorBgBase !te-h-auto !te-min-h-[40px] !te-py-2",
                label: (_jsxs("div", { className: "te-flex te-flex-col", children: [_jsx(Typography.Text, { strong: true, children: name }), _jsx(Typography.Text, { "data-testid": "test-role-text", type: "secondary", children: email || role })] }))
            }
        ];
        if (profiles && authUrl && customerSignature) {
            children.push({ type: "divider" }, {
                key: "user-profiles-options",
                label: (_jsx(Typography.Link, { href: `${authUrl}/${customerSignature}/profile`, children: labels.switchProfile })),
                icon: _jsx(UserSwitchOutlined, {})
            }, { type: "divider" });
        }
        if (onLogout) {
            children.push({
                key: "logout",
                icon: _jsx(LogoutOutlined, {}),
                onClick: onLogout,
                label: labels.logOut
            });
        }
        return children;
    }, []);
    return (_jsx(Menu, { items: [
            {
                icon: _jsx(UserOutlined, {}),
                label: labels.account,
                key: "Account",
                children: userNavigationActionChildren
            }
        ], mode: "inline" }));
};
